<template>
  <b-row class="mt-0">
    <b-col
      :class="{ 'not-valid': !isValid }"
      cols="12"
    >
      <general-section
        ref="campaign-section"
        @updateAdvertiser="updateAdvertiser"
        @updateCurrency="updateCurrency"
        @campaignPayloadChanged="updateIsCampaignPayloadEmpty"
        @updateCampaignPayloadIsValid="updateCampaignPayloadIsValid"
      />
      <div class="mb-5">
        <base-help-tooltip>
          <template slot="left">
            <h3>
              Budget & Schedule
            </h3>
          </template>
          <template slot="popover-body">
            <p>
              <b>Campaign Period</b>
              The dates your campaign will run. Select the start and end dates for your campaign. Once it's approved, your campaign will automatically begin delivering impressions on the start date until midnight on the end date.
            </p>
            <p>
              <b>Campaign Budget (excl. VAT)</b>
              Enter the total budget for the campaign period, excluding VAT. This is the maximum amount the campaign will spend over the campaign period.
              <br>
              Example: If you plan to spend £100 per day for 7 days, enter £700 as your total budget.
            </p>
            <p>
              <b>Note:</b> VAT will be added for UK-registered companies.
            </p>
          </template>
        </base-help-tooltip>
        <div
          v-if="GET_IS_MANAGED_CAMPAIGN"
          class="mb-3"
        >
          <b-row>
            <b-col
              lg="5"
              xl="4"
            >
              <div
                class="d-flex align-items-center mt-5"
              >
                <b-form-checkbox
                  id="add-impression-target-checkbox"
                  name="add-impression-target-checkbox"
                  v-model="isAddImpressionTargetChecked"
                  @change="toggleAddImpressionTarget"
                  size="xl"
                  class="add-impression-target-checkbox"
                  data-test="add-impression-target-checkbox"
                  :disabled="isAddImpressionTargetCheckboxDisabled"
                />
                <div
                  class="text-inherit"
                  data-test="add-impression-target-name"
                >
                  Add impression target
                </div>
              </div>
            </b-col>
          </b-row>
        </div>
        <div
          v-for="(period, index) in newCampaign.periods"
          :key="index + campaignPeriodControlsKey"
          class="mb-1"
        >
          <campaign-period-controls
            :class="{'mb-2': (index !== newCampaign.periods.length - 1)}"
            :ref="`campaign-period-controls-${index}`"
            :key="index"
            :periods="newCampaign.periods"
            :period-index="index"
            :period="period"
            :is-add-impression-target-checked="newCampaign.isAddImpressionTargetChecked"
            @updatePeriod="updatePeriods(index, ...arguments)"
            @deletePeriod="deletePeriod(index)"
            @updatePeriodsValidation="updatePeriodsValidations(index, ...arguments)"
          />
          <b-row
            v-if="isDormantPeriod(index)"
            class="gutters-0 my-3"
          >
            <b-col cols="auto">
              <p
                cols="auto"
                class="alert alert-sm alert-info mb-0"
              >
                Dormant Period {{ getDormantPeriod(period, newCampaign.periods[index + 1]) }}
                <button
                  class="btn ml-2 alert-info btn-info"
                  v-if="isCurrentDormantPeriod(period, newCampaign.periods[index + 1])"
                  @click="openDormantCampaignToLiveModal()"
                >
                  Overwrite Dormant State
                  <i class="ml-2 fa fa-rotate-exclamation" />
                </button>
              </p>
            </b-col>
          </b-row>
        </div>
        <b-row class="gutters-0">
          <b-col cols="auto">
            <p
              :class="['mb-0 mt-2 ', [isAddPeriodDisabled ? 'cursor-not-allowed' : 'cursor-pointer' ]]"
              @click="addPeriod"
              :disabled="isAddPeriodDisabled"
            >
              Add additional campaign period <span class="ml-1"><i class="fal fa-plus" /></span>
            </p>
          </b-col>
        </b-row>
      </div>
      <div class="mb-5">
        <base-help-tooltip>
          <template slot="left">
            <h3
              class=""
              data-test="targeting-label"
            >
              Targeting
            </h3>
          </template>
          <template slot="popover-body">
            <p>
              The countries or regions in which you wish to target your advert.
              Select countries one by one using the green tick icon or a whole
              region and exclude individual countries using the red 'X' icon.
              Collapse or expand a region using the &#8743; or &#8744; and use
              'select all' or 'deselect all' to control the inclusion of all countries in a region.
            </p>
          </template>
        </base-help-tooltip>
        <b-row class="mb-5">
          <b-col>
            <b-form-group
              id="fieldset-7"
              label="Countries"
              label-for="input-7"
              label-class="form-label"
            >
              <template
                slot="label"
              >
                Countries <span class="form-required">*</span>
              </template>

              <region-countries-list
                :targeting="geoTargeting"
                :order-payload="this.orderPayload"
                ref="regionCountriesList"
                @addCountry="addCountry"
                @addRegion="addRegion"
                @removeRegion="removeRegion"
                @updateIsOrderChanged="updateIsOrderChanged"
                :read-only="GET_IS_CAMPAIGN_READONLY"
              />
            </b-form-group>
            <div
              class="tags"
              data-test="geo-targeting-tags"
            >
              <template v-if="hasRegionTargeting">
                <h6>Region</h6>
                <span
                  v-for="(id) in geoTargeting.regions"
                  :key="`region-${id}`"
                  class="tag tag-primary"
                >
                  {{ getRegionName(id) }}
                  <a
                    href="#"
                    class="tag-addon"
                    @click.prevent="removeRegion(id)"
                  >
                    <i class="fa-light fa-xmark" />
                  </a>
                </span>
              </template>
              <template v-if="geoTargeting.countryInclusionsExclusions.inclusions.length > 0">
                <h6 class="mt-3">
                  Include
                </h6>
                <span
                  v-for="(item, index) in geoTargeting.countryInclusionsExclusions.inclusions"
                  :key="`include-${index}`"
                  class="tag tag-success include"
                >
                  {{ getCountryName(item) }}
                  <a
                    href="#"
                    class="tag-addon"
                    @click.prevent="deleteTargeting('inclusions',item)"
                    :disabled="GET_IS_CAMPAIGN_READONLY"
                  >
                    <i class="fa-light fa-xmark" />
                  </a>
                </span>
              </template>
              <template v-if="geoTargeting.countryInclusionsExclusions.exclusions.length > 0">
                <h6 class="mt-3">
                  Exclude
                </h6>
                <span
                  v-for="(item, index) in geoTargeting.countryInclusionsExclusions.exclusions"
                  :key="`exclude-${index}`"
                  class="tag tag-danger exclude"
                >
                  {{ getCountryName(item) }}
                  <a
                    href="#"
                    class="tag-addon"
                    @click.prevent="deleteTargeting('exclusions', item)"
                    :disabled="GET_IS_CAMPAIGN_READONLY"
                  >
                    <i class="fa-light fa-xmark" />
                  </a>
                </span>
              </template>
            </div>
          </b-col>
        </b-row>
      </div>
      <div class="mb-5">
        <brand-safety
          ref="brand-safety"
          @keywordsListChanged="updateIsKeywordsListIsChanged"
          @updateKeyWordsValid="updateBrandSafetyValid"
        />
      </div>
      <div>
        <base-help-tooltip>
          <template slot="left">
            <h3
              class=""
              data-test="targeting-label"
            >
              Publisher Verticals
            </h3>
          </template>
          <template slot="popover-body">
            <p>
              The list of publishers your advert will appear on during the campaign. Please select as many verticals as you wish (at least one vertical must be selected to proceed).
              Please only select the ‘French’ or ‘German’ verticals if your adverts are in either of those languages.
            </p>
          </template>
        </base-help-tooltip>
        <custom-publishers
          ref="customPublishersSelector"
          :order-payload="this.orderPayload"
          :order-set="this.order"
          :disabled="GET_IS_CAMPAIGN_READONLY"
          @updateIsOrderChanged="updateIsOrderChanged"
          @updatePublishersCreativesState="updatePublishersCreativesState"
          @removePublisherFromPublishersCreativesList="removePublisherFromPublishersCreativesList"
        />
      </div>
    </b-col>
    <dormant-to-live-modal
      v-if="!GET_IS_NEW_CAMPAIGN"
      :advertiser-id="campaign.advertiserId"
      :campaign-id="campaign.id"
      @updateState="updateState"
    />
  </b-row>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';
import suppressibleBox from '@/core/mixins/suppressibleBox';
import currenciesMixin from '@/core/mixins/currencies';

import _ from 'lodash';
import helper from '@/core/helper';
import dateHelper from '@/core/mixins/dateHelper';
import CampaignPeriodControls from '@/modules/campaigns/components/CampaignInfo/CampaignPeriodControls';
import DormantToLiveModal from '@/modules/campaigns/components/CampaignInfo/DormantCampaignToLiveConfirmationModal';
import RegionCountriesList from '@/modules/campaigns/components/RegionCountriesList';
import CustomPublishers from '@/modules/campaigns/components/CustomPublishers';
import GeneralSection from '@/modules/campaigns/components/GeneralSection';
import BaseHelpTooltip from '@/components/BaseHelpTooltip.vue';

import {
  parseISO, addDays, subDays, differenceInDays, isPast,
} from 'date-fns';
import BrandSafety from '@/modules/campaigns/components/CampaignInfo/BrandSafety';

export default {
  name: 'CampaignInfo',
  mixins: [
    currenciesMixin,
    suppressibleBox,
    dateHelper,
  ],
  components: {
    BrandSafety,
    BaseHelpTooltip,
    CampaignPeriodControls,
    RegionCountriesList,
    CustomPublishers,
    GeneralSection,
    DormantToLiveModal,
  },
  data() {
    return {
      isCampaignPayloadEmpty: true,
      isOrderPayloadEmpty: true,
      hasKeywordListChanged: false,
      newCampaign: {
        name: '',
        currencyId: 2,
        budget: null,
        start: null,
        end: null,
        periods: [
          {
            budget: null,
            impressionTarget: null,
            startDate: null,
            endDate: null,
          },
        ],
        type: null,
        accountManagerUserId: null,
        salesUserId: null,
        isAddImpressionTargetChecked: false,
      },
      newPeriodsValidations: {},
      options: [],
      isLoading: false,
      geoTargeting: {
        regions: [],
        countryInclusionsExclusions: {
          inclusions: [],
          exclusions: [],
        },
      },
      orderPayload: {},
      publishersGroups: {},
      selectedAdvertiser: {
        name: '',
      },
      campaignNameValid: null,
      updatedPublishersCreatives: [],
      campaignPeriodControlsKey: 0,
      isBrandSafetyValid: false,
      isCampaignPayloadValid: false,
      isOrderPayloadValid: false,
    };
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
      'order',
      'linkingWords',
      'countries',
      'regions',
      'blacklist',
      'publisherIds',
      'selectedPublisherGroups',
    ]),
    ...mapState('content', [
      'creatives',
    ]),
    ...mapState('account', {
      accountId: 'id',
    }),
    ...mapGetters('campaigns', [
      campaignTypes.GET_IS_NEW_CAMPAIGN,
      campaignTypes.GET_IS_MANAGED_CAMPAIGN,
      campaignTypes.GET_IS_VIEW_TYPE,
      campaignTypes.GET_IS_CAMPAIGN_READONLY,
      campaignTypes.GET_CAMPAIGN_TOTAL_BUDGET,
      campaignTypes.GET_CAMPAIGN_MIN_START_DATE,
      campaignTypes.GET_CAMPAIGN_MAX_END_DATE,
    ]),
    campaignHasChanges() {
      return this.GET_IS_NEW_CAMPAIGN || !this.isCampaignPayloadEmpty;
    },
    orderHasChanges() {
      return this.GET_IS_NEW_CAMPAIGN || !this.isOrderPayloadEmpty;
    },
    blackListHasChanges() {
      return this.GET_IS_NEW_CAMPAIGN || this.hasKeywordListChanged;
    },
    publisherCreativesHaveChanges() {
      return this.updatedPublishersCreatives.length > 0;
    },
    isAddImpressionTargetCheckboxDisabled() {
      if (this.GET_IS_CAMPAIGN_READONLY) return true;
      if (!this.isAddImpressionTargetChecked) return false;

      return this.newCampaign.periods
        .filter((period) => period.impressionTarget !== null)
        .filter((period) => this.hasPeriodEnded(period.startDate, period.endDate) > 0)
        .length > 0;
    },
    arePeriodsValid() {
      for (const period of this.newCampaign.periods) {
        for (const property in period) {
          if (property !== 'impressionTarget' && period[property] == null) return false;
        }
      }
      return !Object.values(this.newPeriodsValidations).includes(false);
    },
    isAddPeriodDisabled() {
      return this.GET_IS_CAMPAIGN_READONLY
      || (this.newCampaign.periods.length
          && !this.hasPeriodRequiredFields(this.newCampaign.periods[this.newCampaign.periods.length - 1]));
    },
    isValid() {
      if (this.isLoading) return false;

      const valid = this.isCampaignPayloadValid
        && this.isOrderPayloadValid
        && this.isBrandSafetyValid;

      this.$emit('setNotValidForNavigation', 'last', !valid);
      this.$emit('setNotValidForNavigation', 'next', !valid);
      return valid;
    },
    isGeoTargetingPresent() {
      return this.geoTargeting.regions.length > 0
      || this.geoTargeting.countryInclusionsExclusions.inclusions.length > 0;
    },
    hasIncludeTargeting() {
      return (this.geoTargeting.countryInclusionsExclusions.inclusions.length !== 0);
    },
    hasExcludeTargeting() {
      return (this.geoTargeting.countryInclusionsExclusions.exclusions.length !== 0);
    },
    hasRegionTargeting() {
      return this.geoTargeting.regions.length > 0;
    },
    currentOrderDetails() {
      return {
        startDate: this.newCampaign.start,
        endDate: this.newCampaign.end,
        budget: this.newCampaign.budget,
        periods: this.newCampaign.periods,
        currencyId: this.newCampaign.currencyId,
        geoTargeting: this.geoTargeting,
        publisherIds: this.publisherIds,
      };
    },
    publisherGroupName() {
      return this.GET_IS_MANAGED_CAMPAIGN ? 'MANAGED_SECTOR' : 'SECTOR';
    },
    hasPeriodsChanged() {
      if (!this.order.hasOwnProperty('periods')) return false;
      if (this.newCampaign.periods.length !== this.order.periods.length) return true;
      const isEqualValue = (value1, value2) => (value1 === value2);
      for (let index = 0; index < this.newCampaign.periods.length; index++) {
        if (!isEqualValue(this.newCampaign.periods[index].budget, this.order.periods[index].budget)
          || !isEqualValue(this.newCampaign.periods[index].startDate, this.order.periods[index].startDate)
          || !isEqualValue(this.newCampaign.periods[index].endDate, this.order.periods[index].endDate)
          || !isEqualValue(this.newCampaign.periods[index].impressionTarget, this.order.periods[index].impressionTarget)) {
          return true;
        }
      }
      return false;
    },
    isAddImpressionTargetChecked: {
      get() {
        if (!this.order.periods) return false;
        const doPeriodsContainImpressionTarget = this.order.periods.some((period) => period.impressionTarget);
        this.updateIsAddImpressionTargetChecked(doPeriodsContainImpressionTarget);
        return doPeriodsContainImpressionTarget;
      },
      set(val) {
        this.value = val;
      },
    },
  },
  methods: {
    ...mapActions('campaigns', [
      campaignTypes.DELETE_CAMPAIGN,
      campaignTypes.DELETE_FEED,
      campaignTypes.CREATE_ORDER,
      campaignTypes.DELETE_ORDER,
      campaignTypes.PATCH_ORDER,
      campaignTypes.FETCH_COUNTRIES,
      campaignTypes.FETCH_PUBLISHERS_GROUPS,
      campaignTypes.FETCH_EXISTING_CONTENT_PROVIDER,
      campaignTypes.CREATE_DEAL_OVERRIDES,
      campaignTypes.UPDATE_DEAL_OVERRIDES,
      campaignTypes.GET_CAMPAIGN_BY_NAME,
      campaignTypes.UPDATE_PUBLISHERS_CREATIVES,
      campaignTypes.SET_CAMPAIGN_INFO_CHANGES,
      campaignTypes.CREATE_ADVERTISER,
    ]),
    updateCampaignPayloadIsValid(isValid) {
      this.isCampaignPayloadValid = isValid;
    },
    getIsOrderPayloadValid() {
      if (!this.GET_IS_NEW_CAMPAIGN) return true;
      if (!this.orderPayload.publisherIds || this.orderPayload.publisherIds.length === 0) return false;
      if (!this.arePeriodsValid) return false;
      if (!this.isGeoTargetingPresent) return false;
      return true;
    },
    updateIsOrderPayloadValid() {
      this.isOrderPayloadValid = this.getIsOrderPayloadValid();
    },
    updateSetIsDirty() {
      this.$emit('setIsDirty', this.campaignHasChanges || this.orderHasChanges
        || this.blackListHasChanges || this.publisherCreativesHaveChanges);
    },
    updateIsCampaignPayloadEmpty(isEmpty) {
      this.isCampaignPayloadEmpty = isEmpty;
      this.updateSetIsDirty();
    },
    updateIsOrderPayloadEmpty(isEmpty) {
      this.isOrderPayloadEmpty = isEmpty;
      this.updateSetIsDirty();
    },
    updateIsKeywordsListIsChanged(isChanged) {
      this.hasKeywordListChanged = isChanged;
      this.updateSetIsDirty();
    },
    updateIsOrderChanged() {
      this.updateIsOrderPayloadValid();
      this.updateIsOrderPayloadEmpty(this.isOrderPayloadEmptyCheck());
    },
    isOrderPayloadEmptyCheck() {
      const values = Object.values(this.orderPayload);
      if (values.length === 0) {
        return true;
      }
      return values.every((value) => !value || (Array.isArray(value) && value.length === 0));
    },
    updateIsAddImpressionTargetChecked(doPeriodsContainImpressionTarget) {
      this.newCampaign.isAddImpressionTargetChecked = doPeriodsContainImpressionTarget;
    },
    onSubmit(isDirty) {
      if (!this.isValid) return Promise.reject();
      if (!isDirty) return Promise.resolve();
      if (typeof this.campaign.name !== 'undefined') return this.updateCampaign();
      return this.createCampaign();
    },
    createCampaign() {
      return new Promise(async (resolve, reject) => {
        if (this.isLoading) return;
        this.isLoading = true;
        try {
          if (this.selectedAdvertiser.id === -1) {
            await this.CREATE_ADVERTISER(this.selectedAdvertiser.name)
              .then((advertiser) => {
                this.selectedAdvertiser = advertiser;
              });
          }
          await this.FETCH_EXISTING_CONTENT_PROVIDER();

          const campaignResponse = await this.$refs['campaign-section'].createCampaign();
          const createOrderPayload = {
            ...this.orderPayload,
            advertiserId: campaignResponse.advertiserId,
            budget: this.GET_CAMPAIGN_TOTAL_BUDGET,
            startDate: this.GET_CAMPAIGN_MIN_START_DATE,
            endDate: this.GET_CAMPAIGN_MAX_END_DATE,
          };
          const createOrder = await this.CREATE_ORDER(createOrderPayload);
          Promise.all([
            campaignResponse,
            createOrder,
          ]).then(async () => {
            await this.$refs['brand-safety'].saveBlacklist();
            this.isLoading = false;
            this.orderPayload = {};
            this.updateIsKeywordsListIsChanged(false);
            this.updateIsOrderChanged();
            this.$emit('setIsDirty', false);
            resolve();
          });
        } catch (error) {
          this.handleError(error);
          reject(error);
        } finally {
          this.newPeriodsValidations = {};
          this.SET_CAMPAIGN_INFO_CHANGES({});
        }
      });
    },
    patchOrder() {
      if (this.orderHasChanges) return this.PATCH_ORDER(this.orderPayload);
      return Promise.resolve();
    },
    updateCampaign() {
      return new Promise(async (resolve, reject) => {
        this.isLoading = true;
        try {
          const updateCampaignRequest = this.$refs['campaign-section'].updateCampaign();
          const patchOrder = this.patchOrder();
          const updateBlacklist = this.$refs['brand-safety'].saveBlacklist();
          Promise.all([
            updateCampaignRequest,
            patchOrder,
            updateBlacklist,
          ]).then(async () => {
            if (this.updatedPublishersCreatives.length) {
              this.patchPublishersCreatives(this.campaign.advertiserId, this.campaign.id);
            }
            this.orderPayload = {};
            this.updateIsOrderChanged();
            this.updateIsKeywordsListIsChanged(false);
            this.$emit('setIsDirty', false);
            resolve();
          });
        } catch (error) {
          reject(error);
        } finally {
          this.isLoading = false;
          this.newPeriodsValidations = {};
          this.SET_CAMPAIGN_INFO_CHANGES({});
        }
      });
    },
    getCountryName(name) {
      return this.countries[name];
    },
    getRegionName(id) {
      if (!this.regions.find((i) => i.id === id)) return 'Name not found';
      return this.regions.find((i) => i.id === id).name;
    },
    deleteTargeting(listName, item) {
      if (!this.GET_IS_CAMPAIGN_READONLY) {
        const index = this.geoTargeting.countryInclusionsExclusions[listName]
          .findIndex((i) => i === item);
        if (index !== -1) this.geoTargeting.countryInclusionsExclusions[listName].splice(index, 1);
        this.$refs.regionCountriesList.setTargeting();
        this.updateGeoTargetingPayload();
      }
    },
    addCountry(label, country, regionId) {
      const oppositeList = (label === 'include') ? 'exclusions' : 'inclusions';
      const oppositeListIndex = this.geoTargeting.countryInclusionsExclusions[oppositeList]
        .findIndex((i) => i === country);
      if (oppositeListIndex !== -1) {
        this.geoTargeting.countryInclusionsExclusions[oppositeList]
          .splice(oppositeListIndex, 1);
      }
      const list = (label === 'include') ? 'inclusions' : 'exclusions';
      const index = this.geoTargeting.countryInclusionsExclusions[list]
        .findIndex((i) => i === country);
      if (list === 'exclusions' || (list === 'inclusions' && !this.geoTargeting.regions.some((i) => i === regionId))) {
        if (index === -1) this.geoTargeting.countryInclusionsExclusions[list].push(country);
      }
      this.updateGeoTargetingPayload();
    },
    addRegion(id) {
      this.geoTargeting.countryInclusionsExclusions
        .inclusions = this.geoTargeting.countryInclusionsExclusions.inclusions
          .filter((country) => !this.regions.find((i) => i.id === id).countries.includes(country));

      const index = this.geoTargeting.regions.some((i) => i === id);
      if (index !== -1) this.geoTargeting.regions.push(id);
      this.$refs.regionCountriesList.setTargeting();
      this.updateGeoTargetingPayload();
    },
    removeRegion(id) {
      if (!this.GET_IS_CAMPAIGN_READONLY) {
        const index = this.geoTargeting.regions.findIndex((i) => i === id);
        if (index !== -1) this.geoTargeting.regions.splice(index, 1);
        this.$refs.regionCountriesList.setTargeting();
        this.updateGeoTargetingPayload();
      }
    },
    updateGeoTargetingPayload() {
      if (this.isGeoTargetingChanged()) this.orderPayload.geoTargeting = this.geoTargeting;
      else delete this.orderPayload.geoTargeting;
      this.updateIsOrderChanged();
    },
    async populatePageForExistingCampaign() {
      ['name', 'type', 'accountManagerUserId', 'salesUserId'].forEach((key) => {
        if (this.campaign[key]) this.newCampaign[key] = this.campaign[key];
      });
    },
    updateCurrency(id) {
      this.newCampaign.currencyId = id;
      if (id !== this.order.currencyId) this.orderPayload.currencyId = id;
      else delete this.orderPayload.currencyId;
      this.updateIsOrderChanged();
    },
    resetPeriods() {
      this.$set(this.newCampaign, 'periods', this.assignPeriods(this.order));
      this.campaignPeriodControlsKey++;

      this.SET_CAMPAIGN_INFO_CHANGES({
        target: 'order',
        property: 'periods',
        value: [...this.newCampaign.periods],
      });
    },
    assignPeriods(order) {
      if (order.periods?.length) return _.cloneDeep(order.periods);
      return [{
        budget: order.budget ?? null,
        startDate: order.startDate ?? null,
        endDate: order.endDate ?? null,
      }];
    },
    updatePeriods(index, newPeriod) {
      this.newCampaign.periods.splice(index, 1, newPeriod);
      if (this.hasPeriodsChanged || this.GET_IS_NEW_CAMPAIGN) {
        this.SET_CAMPAIGN_INFO_CHANGES({
          target: 'order',
          property: 'periods',
          value: [...this.newCampaign.periods],
        });
      } else {
        this.SET_CAMPAIGN_INFO_CHANGES({
          target: 'order',
          property: 'periods',
          value: null,
        });
      }
      this.updatePeriodsPayload();
    },
    hasPeriodRequiredFields(period) {
      return !!period.startDate
          && !!period.endDate
          && !!period.budget;
    },
    addPeriod() {
      if (this.isAddPeriodDisabled) return;
      this.newCampaign.periods.push({
        budget: null,
        startDate: null,
        endDate: null,
      });
      this.$set(this.newPeriodsValidations, `${this.newCampaign.periods.length - 1}`, false);
    },
    deletePeriod(index) {
      this.newCampaign.periods.splice(index, 1);
      delete this.newPeriodsValidations[index];
      this.SET_CAMPAIGN_INFO_CHANGES({
        target: 'order',
        property: 'periods',
        value: [...this.newCampaign.periods],
      });
      this.campaignPeriodControlsKey++;
      this.updatePeriodsPayload();
    },
    updatePeriodsPayload() {
      if (this.GET_IS_NEW_CAMPAIGN) {
        this.orderPayload.periods = this.newCampaign.periods;
      } else if (this.hasPeriodsChanged) this.orderPayload.periods = this.newCampaign.periods;
      else delete this.orderPayload.periods;
      this.updateIsOrderChanged();
    },
    isDormantPeriod(index) {
      if (index === this.newCampaign.periods.length - 1 || this.newCampaign.periods.length < 2) return false;
      const { endDate } = this.newCampaign.periods[index];
      const { startDate } = this.newCampaign.periods[index + 1];
      return differenceInDays(parseISO(startDate), parseISO(endDate)) > 1;
    },
    getDormantPeriod(currentPeriod, nextPeriod) {
      return `${this.formatDateForPlatform(addDays(new Date(currentPeriod.endDate), 1))} - ${this.formatDateForPlatform(subDays(new Date(nextPeriod.startDate), 1))}`;
    },
    updatePeriodsValidations(index, isValid) {
      if (this.newPeriodsValidations[index]) {
        this.newPeriodsValidations[index] = isValid;
        return;
      }
      this.$set(this.newPeriodsValidations, `${index}`, isValid);
    },
    populatePageForExistingOrder() {
      this.newCampaign.periods = this.assignPeriods(this.order);
      if (this.GET_IS_NEW_CAMPAIGN) return;
      this.newCampaign.budget = this.order.budget ?? null;
      this.newCampaign.currencyId = this.order.currencyId ?? 2;
      this.newCampaign.start = this.order.startDate ? helper.parseISOString(this.order.startDate) : null;
      this.newCampaign.end = this.order.endDate ? helper.parseISOString(this.order.endDate) : null;
      if (typeof this.order.geoTargeting !== 'undefined') {
        this.geoTargeting = _.cloneDeep(this.order.geoTargeting);
      }
    },
    isGeoTargetingChanged() {
      if (this.GET_IS_NEW_CAMPAIGN) return true;
      const normalize = (geoTargeting) => {
        const clone = _.cloneDeep(geoTargeting);
        clone.regions.sort((a, b) => a - b);
        clone.countryInclusionsExclusions.inclusions.sort((a, b) => a - b);
        clone.countryInclusionsExclusions.exclusions.sort((a, b) => a - b);
        return JSON.stringify(clone);
      };
      return normalize(this.geoTargeting) !== normalize(this.order.geoTargeting);
    },
    toggleAddImpressionTarget(isChecked) {
      this.newCampaign.isAddImpressionTargetChecked = isChecked;
      if (!isChecked) {
        for (let i = 0; i < this.newCampaign.periods.length; i++) {
          this.newCampaign.periods[i].impressionTarget = null;
          this.$refs[`campaign-period-controls-${i}`][0].updateImpressionTarget(null);
        }
      }
    },
    updateOrderDetails() {
      this.$emit('updateOrderDetails', this.currentOrderDetails);
    },
    handleError() {
      this.$nextTick(async () => {
        await this.DELETE_ORDER();
        await this.DELETE_CAMPAIGN();
        this.isLoading = false;
      });
    },
    updateBrandSafetyValid(valid) {
      this.isBrandSafetyValid = valid;
    },
    async updateAdvertiser(advertiser) {
      this.selectedAdvertiser = advertiser;
    },
    updateCampaignNameValid(isValid) {
      this.campaignNameValid = isValid;
    },
    updatePublishersCreativesState(updatedItem, defaultPublisherCreative) {
      if (!this.updatedPublishersCreatives.length) {
        const updatedArray = this.updatedPublishersCreatives.push(updatedItem);
        this.updateSetIsDirty();
        return updatedArray;
      }
      this.updatedPublishersCreatives.forEach((aCreative, index) => {
        const isEqualCreativeId = aCreative.creativeId === updatedItem.creativeId;
        const isEqualPublisherId = aCreative.publisherId === updatedItem.publisherId;
        if (isEqualCreativeId && isEqualPublisherId) this.updatedPublishersCreatives.splice(index, 1);
      });
      if (!defaultPublisherCreative) {
        return updatedItem.isPaused ? this.updatedPublishersCreatives.push(updatedItem) : this.updatedPublishersCreatives;
      }
      const defaultIsPaused = defaultPublisherCreative[0].isPaused;
      this.updateSetIsDirty();
      return defaultIsPaused === updatedItem.isPaused ? this.updatedPublishersCreatives : this.updatedPublishersCreatives.push(updatedItem);
    },
    removePublisherFromPublishersCreativesList(id) {
      this.updatedPublishersCreatives.forEach((aCreative, index) => {
        if (aCreative.publisherId === id) return this.updatedPublishersCreatives.splice(index, 1);
      });
      this.updateSetIsDirty();
    },
    patchPublishersCreatives(advertiserId, campaignId) {
      const updatedCreatives = {
        advertiserId,
        campaignId,
        updatedValues: this.updatedPublishersCreatives,
      };
      const response = this.UPDATE_PUBLISHERS_CREATIVES(updatedCreatives);
      if (response.status === 200) this.updatedPublishersCreatives = [];
    },
    hasPeriodEnded(startDate, endDate) {
      if (!startDate || !endDate) return false;
      return isPast(new Date(endDate)) && isPast(new Date(endDate));
    },
    openDormantCampaignToLiveModal() {
      this.$root.$emit('bv::show::modal', `dormantToLiveModal-${this.campaign.id}`);
    },
    isCurrentDormantPeriod(currentPeriod, nextPeriod) {
      return this.isDatePeriodCurrent(addDays(new Date(currentPeriod.endDate), 1), subDays(new Date(nextPeriod.startDate), 1));
    },
    updateState() {
      this.$router.go(0);
    },
    populatePageForExistingEntities() {
      this.FETCH_PUBLISHERS_GROUPS(this.publisherGroupName)
        .then((groups) => {
          this.publishersGroups = groups;
        });
      this.$nextTick(async () => {
        Promise.all([
          await this.populatePageForExistingCampaign(),
          await this.populatePageForExistingOrder(),
        ]).finally(() => {
          this.isLoading = false;
          this.$emit('setIsDirty', this.campaignHasChanges || this.orderHasChanges || this.blackListHasChanges);
        });
      });
    },
  },
  async mounted() {
    this.newCampaign = {
      name: '',
      budget: null,
      start: null,
      end: null,
      periods: [],
      type: null,
      currencyId: this.currencyId ? this.currencyId : 2,
      accountManagerUserId: null,
      salesUserId: null,
      isAddImpressionTargetChecked: false,
    };
    this.populatePageForExistingEntities();
  },
  watch: {
    campaign() {
      this.populatePageForExistingCampaign();
    },
    order() {
      this.populatePageForExistingOrder();
    },
    currentOrderDetails() {
      this.updateOrderDetails();
    },
  },
};
</script>
<style lang="scss" scoped>
.tag-addon[disabled] {
  cursor: not-allowed !important;
  opacity: 0.65;
}
</style>
